import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listWebsites } from "@/lib/api";
import AddWebsiteDialog from "@/components/AddWebsiteDialog";
import { ArrowUpRight, Globe, Target, ChartLineUp } from "@phosphor-icons/react";

const EMPTY_IMG =
  "https://static.prod-images.emergentagent.com/jobs/2607a866-1734-4153-ba40-4a4b2827589f/images/addd663358f7487af63eb8b85c15328cc734165d79581a69f9eb6cd589136857.png";

const MAX_WEBSITES = 30;

function StatCell({ label, value, sub, testid }) {
  return (
    <div className="border-l border-[var(--rt-border)] pl-4 first:border-l-0 first:pl-0" data-testid={testid}>
      <div className="label-eyebrow">{label}</div>
      <div className="font-display text-3xl mt-1 leading-none">{value}</div>
      {sub && <div className="text-xs text-[var(--rt-text-muted)] mt-1">{sub}</div>}
    </div>
  );
}

function WebsiteCard({ site }) {
  const used = `${site.keyword_count}/200`;
  return (
    <Link
      to={`/websites/${site.id}`}
      data-testid={`website-card-${site.domain}`}
      className="group block border border-[var(--rt-border)] bg-white p-5 hover:bg-[var(--rt-surface-hover)] transition-colors duration-150"
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="label-eyebrow">/ site</div>
          <div className="font-display text-xl mt-1 leading-none">{site.name}</div>
          <div className="text-xs text-[var(--rt-text-muted)] mt-1 font-mono-data">{site.domain}</div>
        </div>
        <ArrowUpRight size={18} className="text-[var(--rt-text-muted)] group-hover:text-[var(--rt-primary)] transition-colors" />
      </div>
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[var(--rt-border)]">
        <div>
          <div className="label-eyebrow text-[10px]">KEYWORDS</div>
          <div className="font-mono-data text-lg mt-1">{used}</div>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">AVG POS</div>
          <div className="font-mono-data text-lg mt-1">{site.avg_position ?? "—"}</div>
        </div>
        <div>
          <div className="label-eyebrow text-[10px]">TOP 10</div>
          <div className="font-mono-data text-lg mt-1">{site.in_top_10}</div>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { data: sites = [], isLoading } = useQuery({
    queryKey: ["websites"],
    queryFn: listWebsites,
  });

  const totalKeywords = sites.reduce((s, w) => s + (w.keyword_count || 0), 0);
  const totalTop10 = sites.reduce((s, w) => s + (w.in_top_10 || 0), 0);
  const totalTop3 = sites.reduce((s, w) => s + (w.in_top_3 || 0), 0);
  const avgPositions = sites.flatMap((w) => (w.avg_position ? [w.avg_position] : []));
  const overallAvg =
    avgPositions.length > 0
      ? (avgPositions.reduce((a, b) => a + b, 0) / avgPositions.length).toFixed(1)
      : "—";

  return (
    <div className="max-w-[1600px] mx-auto px-6 py-8">
      {/* Page header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">/ overview</div>
          <h1 className="font-display text-5xl tracking-tight leading-none mt-2">
            Websites
          </h1>
          <p className="text-sm text-[var(--rt-text-muted)] mt-3 max-w-lg">
            Track up to 30 websites with 200 keywords each. Google US desktop. Rankings refreshed weekly.
          </p>
        </div>
        <AddWebsiteDialog disabled={sites.length >= MAX_WEBSITES} />
      </div>

      {/* KPI strip */}
      <div className="border border-[var(--rt-border)] bg-white p-6 grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
        <StatCell testid="kpi-websites" label="WEBSITES" value={`${sites.length}/30`} sub="Tracked targets" />
        <StatCell testid="kpi-keywords" label="KEYWORDS" value={totalKeywords} sub="Across all sites" />
        <StatCell testid="kpi-top3" label="IN TOP 3" value={totalTop3} sub="Premium positions" />
        <StatCell testid="kpi-top10" label="IN TOP 10" value={totalTop10} sub="First page" />
        <StatCell testid="kpi-avg" label="AVG POSITION" value={overallAvg} sub="Across sites" />
      </div>

      {/* Websites grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="border border-[var(--rt-border)] bg-white p-5 h-[160px] animate-pulse" />
          ))}
        </div>
      ) : sites.length === 0 ? (
        <div className="border border-[var(--rt-border)] bg-white p-10 flex flex-col md:flex-row items-center gap-10" data-testid="empty-state">
          <img src={EMPTY_IMG} alt="" className="w-full md:w-64" />
          <div className="flex-1">
            <div className="label-eyebrow">/ get started</div>
            <h2 className="font-display text-3xl leading-tight mt-2">
              Add your first website
            </h2>
            <p className="text-sm text-[var(--rt-text-muted)] mt-3 max-w-md">
              Enter the domain you want to rank-track, then bulk-paste up to 200 keywords.
              We&apos;ll check Google US desktop top 100 and build your history.
            </p>
            <div className="mt-6">
              <AddWebsiteDialog />
            </div>
            <div className="grid grid-cols-3 gap-4 mt-10 max-w-md">
              <div className="flex flex-col items-start gap-1">
                <Globe size={20} weight="duotone" />
                <span className="label-eyebrow text-[10px] mt-1">1. SITE</span>
                <span className="text-xs text-[var(--rt-text-muted)]">Add domain</span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <Target size={20} weight="duotone" />
                <span className="label-eyebrow text-[10px] mt-1">2. KEYWORDS</span>
                <span className="text-xs text-[var(--rt-text-muted)]">Bulk paste</span>
              </div>
              <div className="flex flex-col items-start gap-1">
                <ChartLineUp size={20} weight="duotone" />
                <span className="label-eyebrow text-[10px] mt-1">3. TRACK</span>
                <span className="text-xs text-[var(--rt-text-muted)]">Weekly</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="websites-grid">
          {sites.map((s) => (
            <WebsiteCard key={s.id} site={s} />
          ))}
        </div>
      )}
    </div>
  );
}
